import { useAuftragRepository } from '@Hooks/useRepository';
import { type ApiContainerStockEntry } from '@Model';
import { ContainerStockRepository } from '@Repositories/Auftragsbearbeitung';
import { isOk, useToasts } from '@synqo/frontend-component-library';
import { useCallback, useState } from 'react';

export interface ContainerStockCorrection {
    /** Sets an existing line to the entry, or adds a line when no standing time is given; reports whether it worked. */
    save: (entry: ApiContainerStockEntry, standingTimeId: string | undefined) => Promise<boolean>;
    /** Whether a save is in flight. */
    isSaving: boolean;
}

/**
 * Writes a corrected or added stock line and tells the user what the server said.
 */
// @ai-generated 2026-10-01T15:01:25+00:00 by matthias.gessinger@synqony.com using Claude Sonnet 5.5
export function useContainerStockCorrection(onSaved: () => void): ContainerStockCorrection {
    const containerStock = useAuftragRepository(ContainerStockRepository);
    const { pushAllToasts } = useToasts();
    const [isSaving, setIsSaving] = useState(false);

    const save = useCallback(
        async (entry: ApiContainerStockEntry, standingTimeId: string | undefined) => {
            setIsSaving(true);
            try {
                const result = standingTimeId
                    ? await containerStock.correctStock(standingTimeId, entry)
                    : await containerStock.addToStock(entry);

                pushAllToasts(result.messages);

                if (!isOk(result)) {
                    return false;
                }

                onSaved();

                return true;
            } finally {
                setIsSaving(false);
            }
        },
        [containerStock, onSaved, pushAllToasts],
    );

    return { save, isSaving };
}
